<template>
  <v-dialog v-model="show" persistent @keydown.esc="hide">
    <template v-slot:activator="{ on }">
      <v-btn
        small
        text
        v-on="on"
        :class="isOpenBtnColorPrimary ? 'primary accent-4 white--text' : ''"
      >
        {{ $t("profile.mentorship.actions.showOngoing") }}
      </v-btn>
    </template>

    <v-card>
      <v-card-title>
        <span class="title text--secondary">
          {{ $t("mentorship.actions.title").toUpperCase() }} {{ actions.code }} 
        </span>
        <v-spacer />
        <v-btn icon @click="hide"><v-icon>close</v-icon></v-btn>
      </v-card-title>
      <v-tabs v-model="tab" fixed->
        <v-tab
          v-for="assignedVersion in actions.assignedVersions"
          :key="assignedVersion.id"
        >
          {{
            `${assignedVersion.name.name} ${
              assignedVersion.revision
            } (${$options.filters.date(
              assignedVersion.created
            )}) ${statsToString(
              mentorshipActionsStatsByVersionId.get(assignedVersion.id)
            )}`
          }}
        </v-tab>
      </v-tabs>
      <v-tabs-items v-model="tab" touchless>
        <v-tab-item
          v-for="assignedVersion in actions.assignedVersions"
          :key="assignedVersion.id"
        >
          <v-data-table
            :headers="headers"
            :items="items"
            hide-default-footer
            show-expand
            single-expand
            :expanded.sync="expanded"
            disable-pagination
          >
            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length" style="padding-top: 16px;">
                <div v-html="item.contentLocale.content"></div>
              </td>
            </template>

            <template v-slot:[`item.contentLocale.title`]="{ item }">
              <div class="font-weight-bold">
                {{ item.contentLocale.title }}
              </div>
            </template>

            <template v-slot:[`item.mentor`]="{ item }">
              <div v-if="item.mentor" class="d-flex align-center">
                <span>{{ item.mentor | nameId }}</span>
                <v-btn
                  v-if="item.signature && item.signature.type == 'assign' && flow[item.signature.type].revokeAllowed(item)"
                  icon
                  small
                  class="ml-2"
                  @click="revoke(item.signature)"
                >
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </div>
                <v-btn
                  v-else-if="!item.status && flow['assign'].allowed(item)"
                  small
                  color="primary"
                  @click="assign(item)"
                >
                  {{ $t("profile.mentorship.actions.assign.create.action") }}
                </v-btn>
            </template>

            <template v-slot:[`item.approveSignature`]="{ item }">
              <span v-if="item.approveSignature">
                {{ item.approveSignature.dateTime | date }}</span
              >
            </template>

            <template v-slot:[`item.confirmSignature`]="{ item }">
              <span v-if="item.confirmSignature">
                {{ item.confirmSignature.dateTime | date }}</span
              >
            </template>

            <template v-slot:[`item.action`]="{ item }">
              <v-chip
                v-if="!item.status"
                small
                color="orange lighten-4"
                class="warning--text text--darken-1 text-uppercase mr-1"
              >
                {{ $t("profile.mentorship.actions.assign.action") }}
              </v-chip>
              <v-btn
                v-else-if="
                  (item.status == 'assign' || item.status == 'approve') &&
                    flow[item.next].allowed(item)
                "
                small
                color="primary"
                @click="sign(item, item.next)"
              >
                {{
                  $t(
                    "profile.mentorship.actions." + item.next + ".create.action"
                  )
                }}
              </v-btn>
              <v-chip
                v-else
                small
                :color="
                  item.status && item.status == 'confirm'
                    ? 'green lighten-4'
                    : 'orange lighten-4'
                "
                :class="
                  item.status && item.status == 'confirm'
                    ? 'success--text text--darken-1 text-uppercase mr-1'
                    : 'warning--text text--darken-1 text-uppercase mr-1'
                "
              >
                {{
                  item.status
                    ? $t(
                        "profile.mentorship.actions." + item.status + ".status"
                      )
                    : $t("profile.mentorship.actions." + item.next + ".action")
                }}
              </v-chip>
            </template>

            <template v-slot:[`item.menu`]="{ item }">
              <v-menu
                bottom
                left
                v-if="
                  item.signature &&
                    flow[item.signature.type].revokeAllowed(item)
                "
              >
                <template v-slot:activator="{ on }">
                  <v-btn icon v-on="on">
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>

                <v-list>
                  <!-- revoke last step -->
                  <v-list-item @click="revoke(item.signature)">
                    <v-icon small left>close</v-icon>
                    {{
                      $t(
                        `profile.mentorship.actions.${item.signature.type}.revoke.title`
                      )
                    }}
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
          </v-data-table>
        </v-tab-item>
      </v-tabs-items>

      <employee-selector
        :title="$t('profile.mentorship.assign.create.title')"
        :show.sync="mentorSelector.show"
        @change="mentorSelector.callback"
      />
      <impersonation
        v-if="impersonateDialog.status"
        :show="impersonateDialog.show"
        :title="
          $t(
            `profile.mentorship.actions.${impersonateDialog.status}.create.title`
          )
        "
        :text="
          $t(
            `profile.mentorship.actions.${impersonateDialog.status}.create.text`
          )
        "
        :action="
          $t(
            `profile.mentorship.actions.${impersonateDialog.status}.create.action`
          )
        "
        @confirm="impersonateDialog.callback"
        @cancel="impersonateDialog.show = false"
      />
      <revoke-modal
        v-if="revokeDialog.status"
        :show="revokeDialog.show"
        :title="
          $t(`profile.mentorship.actions.${revokeDialog.status}.revoke.title`)
        "
        :text="
          $t(`profile.mentorship.actions.${revokeDialog.status}.revoke.text`)
        "
        :action="
          $t(`profile.mentorship.actions.${revokeDialog.status}.revoke.action`)
        "
        @confirm="revokeDialog.callback"
        @cancel="revokeDialog.show = false"
      />
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapState } from "vuex";

import EmployeeSelector from "@/components/EmployeeSelector";
import RevokeModal from "@/components/RevokeModal";
import Impersonation from "@/components/Impersonation";

import AssignMentorshipActionQuery from "@/graphql/mutation/mentorshipActions/AssignMentorshipAction.gql";
import ApproveMentorshipActionQuery from "@/graphql/mutation/mentorshipActions/ApproveMentorshipAction.gql";
import ConfirmMentorshipActionQuery from "@/graphql/mutation/mentorshipActions/ConfirmMentorshipAction.gql";
import RevokeMentorshipActionQuery from "@/graphql/mutation/mentorshipActions/RevokeMentorshipAction.gql";
import MyOldAccountsQuery from "@/graphql/queries/MyOldAccounts.gql";

import {
  getSignatureState,
  getSignatureDerivedData,
  getMentorshipActionsStatsByVersionId,
  matchesUserOrOldID,
} from "@/utils/mentorshipActions";
export default {
  props: {
    actions: {
      type: Object,
      required: true,
    },
    menteeId: {
      type: Number,
      required: true,
    },
    mentorshipMentorId: {
      type: Number,
      required: true,
    },
    isOpenBtnColorPrimary: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    EmployeeSelector,
    RevokeModal,
    Impersonation,
  },
  data() {
    return {
      search: "",
      show: false,
      revStatuses: ["confirm", "approve", "assign"],
      expanded: [],
      tab: null,
      refreshAfterClose: false,
      flow: {
        assign: {
          allowed: () =>
            this.role(
              "admin",
              "departmentManager",
              "manager",
              "safetyRepresentative"
            ) ||
            (this.me && this.mentorshipMentorId == this.me.id),
          revokeAllowed: (item) =>
            this.role(
              "admin",
              "departmentManager",
              "manager",
              "safetyRepresentative"
            ) ||
            (this.me && this.mentorshipMentorId == this.me.id) ||
            (this.me && item.mentor && item.mentor.id == this.me.id),
          query: AssignMentorshipActionQuery,
          noPassword: true,
        },
        approve: {
          allowed: (item) => this.me && this.me.id == item.mentor.id,
          revokeAllowed: (item) =>
            (this.me && this.me.id == item.mentor.id) ||
            this.role(
              "admin",
              "departmentManager",
              "manager",
              "safetyRepresentative"
            ) ||
            (this.me && this.me.id == this.mentorshipMentorId) ||
            (this.me && this.me.id == this.menteeId),
          query: ApproveMentorshipActionQuery,
        },

        confirm: {
          allowed: () =>
            matchesUserOrOldID(this.me, this.menteeId, this.myOldAccounts),
          revokeAllowed: () =>
            matchesUserOrOldID(this.me, this.menteeId, this.myOldAccounts),
          query: ConfirmMentorshipActionQuery,
        },
      },
      mentorSelector: {
        show: false,
        oldTag: null,
        callback: () => {},
      },
      impersonateDialog: {
        show: false,
        status: null,
        callback: () => {},
      },

      revokeDialog: {
        show: false,
        status: null,
        callback: () => {},
      },
    };
  },

  computed: {
    ...mapGetters(["locales", "role"]),
    ...mapState(["me"]),

    mentorshipActionsStatsByVersionId() {
      return getMentorshipActionsStatsByVersionId(this.actions) || new Map();
    },

    assignedVersion() {
      return this.actions.assignedVersions[this.tab || 0];
    },

    items() {
      return (
        this.assignedVersion &&
        this.assignedVersion.content.map((c, index) => {
          const signatureState = getSignatureState(
            c.signatures,
            this.revStatuses
          );
          const signatureDerivedData = getSignatureDerivedData(c.signatures);
          return {
            ...c,
            index: index + 1,
            ...signatureState,
            ...signatureDerivedData,
            contentLocale: c.contentLocale,
          };
        })
      );
    },
    headers() {
      return [
        {
          text: "",
          value: "index",
          class: "text-uppercase text-truncate",
          align: "left",
        },
        {
          text: this.$t(
            "profile.mentorship.actions.column.descriptionOfKnowledge"
          ),
          value: "contentLocale.title",
          class: "text-uppercase text-truncate",
          sortable: false,
        },
        {
          text: this.$t("profile.mentorship.actions.column.mentor"),
          value: "mentor",
          class: "text-uppercase text-truncate",
          sortable: false,
        },
        {
          text: this.$t("profile.mentorship.actions.column.mentorApproval"),
          value: "approveSignature",
          class: "text-uppercase text-truncate",
          sortable: false,
        },
        {
          text: this.$t("profile.mentorship.actions.column.menteeConfirmation"),
          value: "confirmSignature",
          class: "text-uppercase text-truncate",
          sortable: false,
        },
        {
          text: this.$t("profile.mentorship.actions.column.action"),
          value: "action",
          class: "text-uppercase text-truncate",
          sortable: false,
        },
        {
          text: "",
          value: "menu",
          sortable: false,
          align: "right",
        },
      ];
    },
  },
  methods: {
    statsToString(stats) {
      if (!stats) return "";
      return `${stats.confirmedCount}/${stats.assignedCount} (${stats.contentLength})`;
    },
    assign(item, mentorId) {
      if (typeof mentorId === "undefined") {
        this.mentorSelector = {
          show: true,
          callback: (mentorId) => this.assign(item, mentorId),
        };
      } else {
        this.$apollo
          .mutate({
            mutation: AssignMentorshipActionQuery,
            variables: {
              employeeId: this.me.id,
              mentorId: mentorId,
              assignedVersionId: item.assignedId,
              contentId: item.id,
              localeContentId: item.contentLocale.id,
            },
          })
          .then((res) => {
            this.updateItem(item, res);
          })
          .catch((err) => {
            this.$store.commit("push_global_message", {
              color: "error",
              text: err,
            });
          })
          .finally(() => {
            this.mentorSelector = {
              show: false,
              callback: () => {},
            };
          });
      }
    },

    sign(item, status, args) {
      if (!args && !this.flow[status].noPassword) {
        this.impersonateDialog = {
          callback: (args) => this.sign(item, status, args),
          status,
          show: true,
        };
      } else {
        this.impersonateDialog = {
          callback: () => {},
          show: false,
        };
        this.$apollo
          .mutate({
            mutation: this.flow[status].query,
            variables: {
              password: args && args.password,
              employeeId: this.me.id,
              mentorId: item.mentor.id,
              assignedVersionId: item.assignedId,
              contentId: item.id,
              localeContentId: item.contentLocale.id,
            },
          })
          .then((res) => {
            this.updateItem(item, res);
          })
          .catch((err) => {
            this.$store.commit("push_global_message", {
              color: "error",
              text: err,
            });
          });
      }
    },

    updateItem(item, res) {
      const updatedItem = { ...item, signatures: res.data.signatures };
      const index = this.assignedVersion.content.findIndex(
        (item) => item.id === updatedItem.id
      );
      if (index !== -1) {
        this.$set(this.assignedVersion.content, index, updatedItem);
      }
      this.refreshAfterClose = true;
    },

    updateItemAfterRevoke(signature) {
      const index = this.assignedVersion.content.findIndex(
        (item) => item.id === signature.contentId
      );
      if (index !== -1) {
        const item = this.assignedVersion.content[index];
        this.$set(this.assignedVersion.content, index, {
          ...item,
          signatures: item.signatures.filter((sig) => sig.id !== signature.id),
        });
      }
      this.refreshAfterClose = true;
    },

    revoke(signature, args) {
      if (!args) {
        this.revokeDialog = {
          callback: (args) => this.revoke(signature, args),
          status: signature.type,
          show: true,
        };
      } else {
        this.revokeDialog = {
          callback: () => {},
          status: null,
          show: false,
        };
        this.$apollo
          .mutate({
            mutation: RevokeMentorshipActionQuery,
            variables: {
              signatureId: signature.id,
              reason: args.reason,
            },
          })
          .then(() => {
            this.updateItemAfterRevoke(signature);
          })
          .catch((err) => {
            this.$store.commit("push_global_message", {
              color: "error",
              text: err,
            });
          });
      }
    },

    hide() {
      if (this.refreshAfterClose) {
        this.$emit("refresh");
        this.refreshAfterClose = false;
      }
      this.show = false;
    },
  },
  apollo: {
    myOldAccounts: {
      query: MyOldAccountsQuery,
    },
  },
};
</script>
